import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ReconnectingWebSocket, type DashboardState } from '../lib/ws';
import { LiveFeed } from '../components/LiveFeed';
import { PredictionHisto, type UniversityVote } from '../components/PredictionHisto';
import { RevealOverlay, type RevealLeader } from '../components/RevealOverlay';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { formatUsd } from '../lib/rtl';
import { STRINGS } from '../lib/i18n';
import { apiUrl, wsUrl } from '../lib/api';

type Tab = 'live' | 'advanced';

type RevealResp = {
  total_2025_sar_billions: number | null;
  top_by_value: { key: string; en: string; ar: string; value_sar: number; count: number } | null;
  top_by_count: { key: string; en: string; ar: string; count: number } | null;
};

export function Dashboard() {
  const [state, setState] = useState<DashboardState | null>(null);
  const [status, setStatus] = useState<'connecting' | 'open' | 'closed'>('connecting');
  const [reveal, setReveal] = useState<RevealResp | null>(null);
  const [tab, setTab] = useState<Tab>('live');
  const [showQr, setShowQr] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);
  const s = STRINGS.dashboard;

  // Pulse the hero row whenever a new query lands so the projector audience
  // sees the dashboard react to their activity.
  const lastQueriesRef = useState<{ n: number }>({ n: 0 })[0];

  useEffect(() => {
    fetch(apiUrl('/api/public/reveal-data'), { credentials: 'include' })
      .then((r) => r.json())
      .then((j: RevealResp) => setReveal(j))
      .catch(() => { /* ignore */ });

    const ws = new ReconnectingWebSocket(wsUrl('/ws/dashboard'));
    const offStatus = ws.onStatus(setStatus);
    let lastWsAt = Date.now();
    const offMsg = ws.onMessage((msg) => {
      if (msg.type === 'state') {
        lastWsAt = Date.now();
        setState(msg.state);
        if (msg.state.total_queries > lastQueriesRef.n) {
          lastQueriesRef.n = msg.state.total_queries;
          setPulseKey((k) => k + 1);
        }
      }
    });
    ws.connect();

    // Polling safety net — if WS hasn't pushed an update in 5s the projector
    // kiosk gets a fresh state via plain HTTP. This means the dashboard never
    // goes stale even if WebSocket silently drops or the worker hibernates a
    // long-idle DO. Cheap (~1 request every 3s, payload < 2KB).
    const pollId = window.setInterval(async () => {
      if (Date.now() - lastWsAt < 5_000) return;
      try {
        const res = await fetch(apiUrl('/api/public/dashboard-state'), { credentials: 'include' });
        if (!res.ok) return;
        const fresh = await res.json() as DashboardState;
        setState(fresh);
        if (fresh.total_queries > lastQueriesRef.n) {
          lastQueriesRef.n = fresh.total_queries;
          setPulseKey((k) => k + 1);
        }
      } catch { /* ignore */ }
    }, 3_000);

    return () => { offStatus(); offMsg(); ws.close(); window.clearInterval(pollId); };
  }, [lastQueriesRef]);

  const votes: UniversityVote[] = state?.predictions_by_university ?? [];
  const totalVotes = state?.predictions_count ?? 0;
  const revealLeader: RevealLeader | null = reveal?.top_by_value
    ? { ar: reveal.top_by_value.ar, en: reveal.top_by_value.en, value_sar: reveal.top_by_value.value_sar, count: reveal.top_by_value.count }
    : null;

  return (
    <div className="min-h-full bg-[#0A1733] dashboard-stage relative overflow-hidden" dir="rtl">
      {/* Animated radial gradients for atmospheric depth */}
      <div className="fixed inset-0 pointer-events-none">
        <motion.div
          className="absolute -top-40 -start-40 w-[700px] h-[700px] rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(45,154,95,0.30), rgba(45,154,95,0))' }}
          animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/3 -end-40 w-[800px] h-[800px] rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(212,168,67,0.18), rgba(212,168,67,0))' }}
          animate={{ x: [0, -50, 0], y: [0, 60, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-40 start-1/4 w-[700px] h-[700px] rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(31,64,112,0.45), rgba(31,64,112,0))' }}
          animate={{ x: [0, 40, 0], y: [0, -50, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Hero header — compact single-row for projector */}
      <header className="relative z-10">
        <div className="max-w-[1700px] mx-auto px-6 lg:px-10 pt-3 pb-2 flex items-center justify-between gap-5 flex-wrap">
          <div className="flex items-center gap-4">
            <motion.div
              className="rounded-xl bg-white p-2 shadow-[0_4px_20px_-6px_rgba(0,0,0,0.5)] ring-1 ring-white/40"
              initial={{ opacity: 0, x: -20, rotate: -3 }}
              animate={{ opacity: 1, x: 0, rotate: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              <img src="/brand/tatweer-white.svg" alt="Tatweer" className="h-10 lg:h-12 w-auto" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="leading-tight">
              <div className="text-2xl lg:text-3xl font-black text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
                مرصد اعتماد
                <span className="ms-3 text-[10px] uppercase tracking-[0.3em] text-tatweer-gold font-bold align-middle">
                  Marsad Etimad <span className="text-white/40 mx-1">·</span> Live Workshop
                </span>
              </div>
            </motion.div>
          </div>

          <motion.div className="flex items-center gap-3 flex-wrap" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <button
              type="button"
              onClick={() => setShowQr(true)}
              className="group inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-tatweer-gold text-tatweer-navy ring-2 ring-tatweer-gold/60 hover:ring-tatweer-gold hover:scale-[1.03] active:scale-[0.98] text-sm font-extrabold transition shadow-[0_4px_20px_rgba(212,168,67,0.4)]"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="1.5" />
                <rect x="14" y="3" width="7" height="7" rx="1.5" />
                <rect x="3" y="14" width="7" height="7" rx="1.5" />
                <path d="M14 14h3v3M20 17v4M14 20h3" />
              </svg>
              <span>{s.qrCta.ar}</span>
            </button>

            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ring-1 backdrop-blur text-sm font-bold ${
              status === 'open'
                ? 'bg-tatweer-green/25 ring-tatweer-green/60 text-white shadow-[0_0_20px_rgba(45,154,95,0.4)]'
                : status === 'connecting'
                ? 'bg-tatweer-gold/25 ring-tatweer-gold/60 text-white'
                : 'bg-rose-500/30 ring-rose-300/60 text-white'
            }`}>
              <span className={`inline-block w-2 h-2 rounded-full ${
                status === 'open' ? 'bg-tatweer-green animate-pulse'
                  : status === 'connecting' ? 'bg-tatweer-gold animate-pulse'
                  : 'bg-rose-400'
              }`} />
              <span>
                {status === 'open' ? s.live.ar : status === 'connecting' ? s.connecting.ar : s.disconnected.ar}
              </span>
            </span>

            {state?.paused && (
              <span className="px-3 py-1.5 rounded-full bg-tatweer-gold/30 text-white ring-1 ring-tatweer-gold/60 text-xs font-bold backdrop-blur">
                {s.paused.ar}
              </span>
            )}
            {state?.circuit_open && (
              <span className="px-3 py-1.5 rounded-full bg-rose-500/40 text-white ring-1 ring-rose-300/60 text-xs font-bold backdrop-blur">
                {s.circuitOpen.ar}
              </span>
            )}
          </motion.div>
        </div>

        {/* Tab strip — compact */}
        <div className="max-w-[1700px] mx-auto px-6 lg:px-10">
          <div className="flex gap-1 border-b border-white/10">
            {(['live', 'advanced'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`relative px-6 py-2 text-sm font-bold tracking-wide transition ${
                  tab === t ? 'text-white' : 'text-white/50 hover:text-white/85'
                }`}
              >
                {t === 'live' ? s.tabs.live.ar : s.tabs.advanced.ar}
                {tab === t && (
                  <motion.span
                    layoutId="tab-underline"
                    className="absolute bottom-0 inset-x-3 h-[3px] bg-gradient-to-r from-tatweer-green via-tatweer-gold to-tatweer-green rounded-full shadow-[0_0_12px_rgba(212,168,67,0.6)]"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-[1700px] mx-auto px-6 lg:px-10 py-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            {tab === 'live' ? (
              <LiveTab
                state={state}
                pulseKey={pulseKey}
                votes={votes}
                totalVotes={totalVotes}
                topByValue={reveal?.top_by_value ?? null}
                topByCount={reveal?.top_by_count ?? null}
              />
            ) : (
              <AdvancedTab state={state} />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {state?.reveal_mode && <RevealOverlay leader={revealLeader} />}
      {showQr && <QrPopup onClose={() => setShowQr(false)} />}
    </div>
  );
}

function LiveTab({
  state,
  pulseKey,
  votes,
  totalVotes,
  topByValue,
  topByCount,
}: {
  state: DashboardState | null;
  pulseKey: number;
  votes: UniversityVote[];
  totalVotes: number;
  topByValue: { key: string; en: string; ar: string; value_sar: number; count: number } | null;
  topByCount: { key: string; en: string; ar: string; count: number } | null;
}) {
  const t = STRINGS.dashboard.tiles;
  return (
    <>
      {/* Hero counter row */}
      <motion.div
        key={pulseKey === 0 ? 'static' : `pulse-${pulseKey}`}
        animate={pulseKey === 0 ? {} : { scale: [1, 1.012, 1] }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4"
      >
        <HeroTile
          labelAr={t.connected.ar}
          labelEn={t.connected.en}
          value={state?.connected ?? 0}
          tint="green"
          live={!!state?.connected}
        />
        <HeroTile
          labelAr={t.queries.ar}
          labelEn={t.queries.en}
          value={state?.total_queries ?? 0}
          tint="navy"
        />
        <HeroTile
          labelAr={t.votes.ar}
          labelEn={t.votes.en}
          value={totalVotes}
          tint="gold"
        />
      </motion.div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <section className="xl:col-span-2">
          <PredictionHisto
            votes={votes}
            totalVotes={totalVotes}
            topByValue={topByValue}
            topByCount={topByCount}
            revealMode={!!state?.reveal_mode}
          />
        </section>

        <section>
          <div className="rounded-2xl bg-white/95 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)] ring-1 ring-white/30 backdrop-blur p-4 h-full">
            <div className="flex items-baseline justify-between mb-3 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-tatweer-navy/70 font-bold">آخر الأسئلة</div>
                <div className="text-base font-extrabold text-tatweer-navy">Live activity feed</div>
              </div>
              <div className="text-[10px] text-tatweer-muted">آخر ٢٠</div>
            </div>
            <LiveFeed items={state?.feed ?? []} />
          </div>
        </section>
      </div>
    </>
  );
}

function AdvancedTab({ state }: { state: DashboardState | null }) {
  const t = STRINGS.dashboard.advancedTiles;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Cost gauge — featured */}
      <section className="lg:col-span-3">
        <div className="rounded-3xl bg-gradient-to-br from-white to-tatweer-light shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)] ring-1 ring-white/30 backdrop-blur p-8 flex items-center justify-between gap-8 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-tatweer-navy/70 font-bold">{t.cost.ar} · {t.cost.en}</div>
            <div className="text-6xl lg:text-7xl font-black tabular-nums bidi-plaintext text-tatweer-navy mt-2 leading-none" dir="ltr">
              {state ? formatUsd(state.budget_used_usd) : '—'}
            </div>
            <div className="text-xs text-tatweer-muted mt-2 tracking-wide">workshop budget cap: $530 · running cost</div>
          </div>
          <div className="flex-1 min-w-[260px] max-w-md">
            <div className="h-4 rounded-full bg-tatweer-light overflow-hidden ring-1 ring-tatweer-line shadow-inner">
              <motion.div
                className={`h-full rounded-full ${
                  (state?.budget_used_usd ?? 0) > 40
                    ? 'bg-gradient-to-r from-rose-400 to-rose-600'
                    : (state?.budget_used_usd ?? 0) > 20
                    ? 'bg-gradient-to-r from-tatweer-gold to-amber-600'
                    : 'bg-gradient-to-r from-tatweer-green to-emerald-600'
                }`}
                initial={false}
                animate={{ width: `${Math.min(100, ((state?.budget_used_usd ?? 0) / 530) * 100)}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-tatweer-muted mt-2 tabular-nums" dir="ltr">
              <span>$0</span>
              <span>$265 (50%)</span>
              <span>$530</span>
            </div>
          </div>
        </div>
      </section>

      <AdvTile labelAr="استعلامات تاريخية" labelEn="Historical lookups" value={state?.mode_a_count ?? 0} sub="Mode A queries" />
      <AdvTile labelAr="تقدير قيمة مشروع" labelEn="Project estimates" value={state?.mode_b_count ?? 0} sub="Mode B queries" />
      <AdvTile labelAr="رفض نطاق" labelEn="Out-of-scope refusals" value={state?.refused_count ?? 0} sub="scope-lock" />

      <AdvTile labelAr={t.cacheHits.ar} labelEn={t.cacheHits.en} value={state?.cache_hits ?? 0} sub="classifier+answer" />
      <AdvTile labelAr={t.cacheSize.ar} labelEn={t.cacheSize.en} value={state?.cache_size ?? 0} sub="entries (TTL 5m)" />
      <AdvTile labelAr={t.feedback.ar} labelEn={t.feedback.en} value={state?.feedback_count ?? 0} sub="user reports" />

      <AdvTile labelAr={t.provider.ar} labelEn={t.provider.en} value={state?.provider === 'anthropic' ? 'Anthropic' : 'OpenAI'} text />
      <AdvTile labelAr={t.modeBModel.ar} labelEn={t.modeBModel.en} value={state?.mode_b_model === 'opus' ? 'Opus 4.7' : 'Sonnet 4.6'} text />
      <AdvTile labelAr="auto-fallback" labelEn="Auto failover" value={state?.auto_fallback_enabled ? 'ON' : 'OFF'} text />

      <AdvTile labelAr={t.throttle.ar} labelEn={t.throttle.en} value={state?.throttle_ms ?? 0} sub="ms / query" />
      <AdvTile labelAr={t.rateLimit.ar} labelEn={t.rateLimit.en} value={state?.per_session_rate_per_min ?? 0} sub="per session" />
      <div className="rounded-2xl bg-white/95 ring-1 ring-white/30 backdrop-blur shadow-[0_10px_40px_-15px_rgba(0,0,0,0.5)] p-6 flex items-center justify-center text-tatweer-muted text-xs uppercase tracking-wider">{state?.testing_mode ? 'TESTING MODE ON' : 'production'}</div>

      {state?.fallback_reason && (
        <section className="lg:col-span-3">
          <div className="rounded-2xl bg-tatweer-gold/15 ring-1 ring-tatweer-gold/50 p-5 text-sm text-white/90 backdrop-blur">
            <span className="font-bold text-tatweer-gold">⚠</span> {state.fallback_reason}
          </div>
        </section>
      )}
    </div>
  );
}

function HeroTile({
  labelAr,
  labelEn,
  value,
  tint,
  live,
}: {
  labelAr: string;
  labelEn: string;
  value: number;
  tint: 'navy' | 'green' | 'gold';
  live?: boolean;
}) {
  // Glassmorphic frosted tile that floats above the dark stage. Each tint has
  // its own subtle accent ring + glow so the eye picks them apart at a glance.
  const accent = {
    navy: 'from-white/95 to-white/85 ring-white/40 text-tatweer-navy',
    green: 'from-tatweer-green-50/95 to-white/85 ring-tatweer-green/40 text-tatweer-green-600',
    gold: 'from-amber-50/95 to-white/85 ring-tatweer-gold/45 text-tatweer-gold',
  }[tint];
  const glow = {
    navy: 'shadow-[0_20px_55px_-18px_rgba(0,0,0,0.55)]',
    green: 'shadow-[0_20px_55px_-18px_rgba(45,154,95,0.45)]',
    gold: 'shadow-[0_20px_55px_-18px_rgba(212,168,67,0.45)]',
  }[tint];
  return (
    <div className={`relative rounded-2xl bg-gradient-to-br ${accent} backdrop-blur ${glow} ring-1 px-6 py-4 overflow-hidden`}>
      {live && (
        <motion.span
          className="absolute top-3 end-4 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-tatweer-green-600"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-tatweer-green opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-tatweer-green" />
          </span>
          LIVE
        </motion.span>
      )}
      <div className="text-base lg:text-lg text-tatweer-navy font-extrabold leading-tight">
        {labelAr}
      </div>
      <div className="text-[10px] lg:text-[11px] uppercase tracking-[0.25em] text-tatweer-muted font-bold mt-0.5">
        {labelEn}
      </div>
      <div className={`text-5xl lg:text-6xl font-black tabular-nums bidi-plaintext mt-2 leading-none ${tint === 'navy' ? 'text-tatweer-navy' : tint === 'green' ? 'text-tatweer-green-600' : 'text-tatweer-gold'}`} dir="ltr">
        <AnimatedCounter value={value} />
      </div>
    </div>
  );
}

function AdvTile({
  labelAr,
  labelEn,
  value,
  sub,
  text,
}: {
  labelAr: string;
  labelEn: string;
  value: number | string;
  sub?: string;
  text?: boolean;
}) {
  return (
    <div className="rounded-2xl bg-white/95 ring-1 ring-white/30 backdrop-blur shadow-[0_10px_40px_-15px_rgba(0,0,0,0.5)] p-6">
      <div className="text-base lg:text-lg text-tatweer-navy font-extrabold leading-tight">{labelAr}</div>
      <div className="text-[11px] uppercase tracking-[0.25em] text-tatweer-muted font-bold mt-1">{labelEn}</div>
      <div className={`font-black tabular-nums text-tatweer-navy mt-2 ${text ? 'text-3xl' : 'text-5xl'}`} dir="ltr">
        {typeof value === 'number' ? <AnimatedCounter value={value} /> : value}
      </div>
      {sub && <div className="text-[11px] text-tatweer-muted mt-1.5">{sub}</div>}
    </div>
  );
}

function QrPopup({ onClose }: { onClose: () => void }) {
  const [origin, setOrigin] = useState('');
  useEffect(() => {
    setOrigin(window.location.origin);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const url = origin ? `${origin}/` : '';
  const qrSrc = origin
    ? `https://api.qrserver.com/v1/create-qr-code/?size=720x720&margin=24&qzone=4&data=${encodeURIComponent(url)}&color=19355C&bgcolor=FFFFFF`
    : '';

  return (
    <motion.div
      className="fixed inset-0 z-50 grid place-items-center bg-tatweer-navy/95 backdrop-blur-md"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="relative bg-white rounded-3xl shadow-2xl p-8 sm:p-10 max-w-3xl w-full mx-4 text-center"
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.92, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 end-4 w-10 h-10 rounded-full text-tatweer-muted hover:text-tatweer-body hover:bg-tatweer-light text-2xl leading-none transition"
        >
          ×
        </button>

        <div className="flex items-center justify-center gap-4 pb-5 border-b border-tatweer-line mb-6">
          <div className="rounded-2xl bg-tatweer-navy p-3 shadow-lg">
            <img src="/brand/tatweer-white.svg" alt="Tatweer" className="h-12 w-auto" />
          </div>
          <div className="text-start">
            <div className="text-[10px] uppercase tracking-[0.3em] text-tatweer-muted font-bold">Scan to join</div>
            <div className="text-2xl font-extrabold text-tatweer-navy mt-0.5">مرصد اعتماد</div>
            <div className="text-xs text-tatweer-body-soft">Marsad Etimad · Live Workshop</div>
          </div>
        </div>

        {qrSrc ? (
          <img
            src={qrSrc}
            alt="QR code"
            className="mx-auto rounded-2xl ring-1 ring-tatweer-line p-2"
            style={{ width: 'min(72vmin, 720px)', height: 'min(72vmin, 720px)' }}
          />
        ) : (
          <div className="bg-tatweer-light rounded-2xl mx-auto" style={{ width: 'min(72vmin, 720px)', height: 'min(72vmin, 720px)' }} />
        )}

        <div className="mt-6 text-xl sm:text-2xl font-mono text-tatweer-navy break-all">{url}</div>
        <div className="mt-3 text-sm text-tatweer-body-soft">
          Press <kbd className="px-1.5 py-0.5 ring-1 ring-tatweer-line rounded text-xs">Esc</kbd> or click outside to close
        </div>
      </motion.div>
    </motion.div>
  );
}
